import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney, fmtDate } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { useAuth } from "@/contexts/AuthContext";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, BarChart, Bar, Legend,
} from "recharts";
import { CurrencyInr, Receipt, Users, Package, Clock, TrendUp } from "@phosphor-icons/react";

const PIE_COLORS = ["#C84B31", "#4E6C50", "#DAB88B", "#9A8C98"];

function KpiCard({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className="bento p-5" data-testid={`kpi-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="label-eyebrow">{label}</div>
          <div className="font-display font-extrabold text-3xl mt-2" data-testid="kpi-value">{value}</div>
          {sub && <div className="text-xs text-[var(--text-secondary)] mt-1">{sub}</div>}
        </div>
        <div className={`w-10 h-10 rounded-md grid place-items-center ${accent || "bg-[var(--bg-secondary)]"}`}>
          <Icon size={20} weight="duotone" />
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [revenue, setRevenue] = useState([]);
  const [topItems, setTopItems] = useState([]);
  const [orderTypes, setOrderTypes] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/analytics/summary").then((r) => setSummary(r.data)).catch(() => {}),
      api.get("/analytics/revenue", { params: { days: 14 } }).then((r) => setRevenue(r.data)).catch(() => {}),
      api.get("/analytics/top-items").then((r) => setTopItems(r.data)).catch(() => {}),
      api.get("/analytics/orders-by-type").then((r) => setOrderTypes(r.data)).catch(() => {}),
      api.get("/orders", { params: { limit: 8 } }).then((r) => setRecentOrders(r.data)).catch(() => {}),
    ]);
  }, []);

  return (
    <>
      <PageHeader
        eyebrow={`Welcome back, ${user?.name?.split(" ")[0] || "team"}`}
        title="Operations Dashboard"
        subtitle="Real-time pulse of your restaurant — revenue, orders, kitchen, and inventory at a glance."
      />

      {/* KPI Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8 stagger" data-testid="dashboard-kpis">
        <KpiCard icon={CurrencyInr} label="Today Revenue" value={fmtMoney(summary?.today_revenue)} sub={`${summary?.today_orders || 0} orders`} accent="bg-[var(--brand)]/10 text-[var(--brand)]" />
        <KpiCard icon={Receipt} label="Active Orders" value={summary?.active_orders ?? "—"} sub="In pipeline" accent="bg-[var(--status-preparing)]/30 text-[#9b6500]" />
        <KpiCard icon={TrendUp} label="Avg Order Value" value={fmtMoney(summary?.avg_order_value)} sub={`${summary?.total_orders || 0} all-time`} accent="bg-[var(--brand-2)]/15 text-[var(--brand-2)]" />
        <KpiCard icon={Package} label="Low Stock Items" value={summary?.low_stock_count ?? "—"} sub="Need restock" accent="bg-[var(--status-pending)]/20 text-[var(--brand)]" />
      </div>

      {/* Bento layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
        <div className="bento p-5 lg:col-span-2" data-testid="revenue-chart-card">
          <div className="flex justify-between items-baseline mb-4">
            <div>
              <div className="label-eyebrow">Revenue trend</div>
              <h3 className="font-display font-bold text-xl mt-1">Last 14 days</h3>
            </div>
            <div className="text-xs text-[var(--text-secondary)]">Paid orders only</div>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={revenue} margin={{ top: 8, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorRev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#C84B31" stopOpacity={0.5} />
                    <stop offset="95%" stopColor="#C84B31" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
                <YAxis tick={{ fontSize: 10 }} />
                <CartesianGrid strokeDasharray="3 3" stroke="#EAE7E0" />
                <Tooltip contentStyle={{ background: "white", border: "1px solid #EAE7E0", borderRadius: 8, fontSize: 12 }} />
                <Area type="monotone" dataKey="revenue" stroke="#C84B31" strokeWidth={2} fill="url(#colorRev)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bento p-5" data-testid="order-types-card">
          <div className="label-eyebrow">Orders by type</div>
          <h3 className="font-display font-bold text-xl mt-1 mb-3">Mix</h3>
          <div className="h-44">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={orderTypes} dataKey="count" nameKey="type" outerRadius={68} innerRadius={42} paddingAngle={2}>
                  {orderTypes.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "white", border: "1px solid #EAE7E0", borderRadius: 8, fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-3 space-y-1">
            {orderTypes.map((o, i) => (
              <div key={o.type} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                  <span className="capitalize font-semibold">{o.type}</span>
                </div>
                <span className="text-[var(--text-secondary)]">{o.count} · {fmtMoney(o.revenue)}</span>
              </div>
            ))}
            {orderTypes.length === 0 && <div className="text-xs text-[var(--text-secondary)]">No orders yet.</div>}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
        {/* Top items */}
        <div className="bento p-5 lg:col-span-1" data-testid="top-items-card">
          <div className="label-eyebrow">Best sellers</div>
          <h3 className="font-display font-bold text-xl mt-1 mb-3">Top items</h3>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={topItems} layout="vertical" margin={{ top: 0, right: 10, left: 30, bottom: 0 }}>
                <XAxis type="number" hide />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={90} />
                <Tooltip contentStyle={{ background: "white", border: "1px solid #EAE7E0", borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="quantity" fill="#4E6C50" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          {topItems.length === 0 && (
            <div className="text-xs text-[var(--text-secondary)]">Place a few orders to see your top sellers here.</div>
          )}
        </div>

        {/* Recent orders */}
        <div className="bento p-5 lg:col-span-2" data-testid="recent-orders-card">
          <div className="flex justify-between items-baseline mb-3">
            <div>
              <div className="label-eyebrow">Live feed</div>
              <h3 className="font-display font-bold text-xl mt-1">Recent orders</h3>
            </div>
            <Link to="/app/orders" className="text-xs font-semibold text-[var(--brand)] hover:underline" data-testid="see-all-orders">See all →</Link>
          </div>
          <div className="space-y-2">
            {recentOrders.map((o) => (
              <div key={o.id} className="flex items-center justify-between p-3 bg-[var(--bg-secondary)]/50 rounded-md" data-testid={`recent-order-${o.id}`}>
                <div>
                  <div className="font-semibold text-sm">{o.order_number}</div>
                  <div className="text-[11px] text-[var(--text-secondary)]">{o.type} · {o.table_number ? `Table ${o.table_number}` : (o.customer_name || "Guest")} · {fmtDate(o.created_at)}</div>
                </div>
                <div className="text-right">
                  <div className="font-display font-bold">{fmtMoney(o.total)}</div>
                  <div className="text-[10px] uppercase tracking-wider font-bold text-[var(--text-secondary)]">{o.status}</div>
                </div>
              </div>
            ))}
            {recentOrders.length === 0 && (
              <div className="text-sm text-[var(--text-secondary)] py-8 text-center">No orders yet. Head to the <Link to="/app/pos" className="text-[var(--brand)] font-semibold underline">POS</Link> to start.</div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
